import Big from "big.js";
import * as z from "zod";
import { type ValidationIssue, validationIssue } from "../index";
import { type ErpInvoiceDirection, erpInvoiceDirections, type TaxDestination } from "../reference/enums";

// The invoices file to the ERP (spec v0.3 §7.4, C-41, ADR-256): the file Business Central creates the purchase and the
// sales invoices from, today imported by hand (§7.5). One row for each line of a document, the columns of the header
// repeated on each row. Nexxt is responsible for the values and their check: the domains give the documents with their
// lines (procurement the supplier invoices, documents the invoices it numbers), and these pure functions compute VAT
// and totals, check the balance and write the text. TIL may import them when the import is automated (D-23, T-76).

/** A file holds one kind, chosen at the import on Business Central: purchase (passive) or sales (active) invoices. */
export const erpInvoiceKinds = erpInvoiceDirections;
export type ErpInvoiceKind = ErpInvoiceDirection;

/** #15: an invoice or a credit note. */
export const erpDocumentTypes = ["INV", "CRN"] as const;
export type ErpDocumentType = (typeof erpDocumentTypes)[number];

/** The columns of a row, in their fixed order (§7.4.2): no header row in the file. */
export const ERP_INVOICE_COLUMNS = 25;

/** #17: the OMS Location Name the sheet validates. TODO(V-20): 10 characters exactly in the sheet (T-84). */
export const ERP_LOCATION_MAX_LENGTH = 10;

const isoDay = z.iso.date();
const amount = z.string().regex(/^-?\d+(\.\d+)?$/);
const code = (max: number) => z.string().min(1).max(max).nullable();

/** A line of a document: one row of the file. */
export const erpInvoiceLine = z.object({
  /** The line of the document in the domain (SO line, PO line): what an issue names. Not in the file. */
  lineNo: z.number().int().nullable(),
  /** #7: the `skuCode` of the item (V-20), or what a line that is not goods is. */
  description: z.string().min(1).max(200),
  /** A line of goods; one that is not (transport, duties, a deposit) names its account (#9). Not in the file. */
  goods: z.boolean(),
  /** #21. */
  quantity: z.number().int(),
  /** #8: the taxable amount of the line, two decimals. */
  base: amount,
  /** #9: the account of the chart of accounts, for a line that is not goods. */
  costAccount: code(20),
  /** #11: the VAT code of Business Central (D-25). */
  vatCode: code(20),
  /** The rate of the VAT code in percent, from the table of Finance: it makes #10; null when the table lacks the code. */
  vatRate: amount.nullable(),
  /** #12: the VAT register (Protocol). */
  protocol: code(20),
  /** #14, #22, #24: the dimensions of Business Central (D-25, V-20). */
  costCentre: code(20),
  project: code(20),
  costCentreCode: code(20),
  /** #17: the warehouse, as Business Central knows it. */
  location: code(40),
  /** #18, #19: the accrual of a service across periods; empty for goods. */
  accrualFrom: isoDay.nullable(),
  accrualTo: isoDay.nullable(),
  /** #20: the PO as Business Central knows it (I-02); empty on a sales invoice. */
  poNumber: code(60),
  /** #23: the season, in the code of Business Central. */
  season: code(20),
  /** #25: empty, Business Central uses the one of its master data. */
  paymentMethod: code(20),
});
export type ErpInvoiceLine = z.output<typeof erpInvoiceLine>;

/** A document of the file: the columns of its header, and its lines. */
export const erpInvoiceDocument = z.object({
  /** #1: the supplier (I-27) or the customer, by its number on Business Central. */
  party: code(40),
  /** #2: the supplier's number of the invoice, or the fiscal number Nexxt gave a sales document (C-48). */
  number: code(60),
  /** #3 and #4. */
  invoiceDate: isoDay,
  postingDate: isoDay,
  /**
   * #5 as the document states it, VAT included: the supplier's total. Null when Nexxt makes the document, whose total
   * is the sum of its lines.
   */
  statedTotal: amount.nullable(),
  /** #6. */
  description: z.string().min(1).max(200),
  documentType: z.enum(erpDocumentTypes),
  /** #16, ISO 4217. */
  currency: z.string(),
  lines: z.array(erpInvoiceLine).max(5000),
});
export type ErpInvoiceDocument = z.output<typeof erpInvoiceDocument>;

/** The rules an issue of a document names (`ValidationIssue.rule`). */
export const erpInvoiceIssueRules = [
  "required",
  "invalid_value",
  "no_lines",
  "missing_vat_code",
  "unknown_vat_code",
  "missing_protocol",
  "missing_cost_account",
  "location_too_long",
  "unbalanced",
  "duplicate_document",
] as const;
export type ErpInvoiceIssueRule = (typeof erpInvoiceIssueRules)[number];

const issue = (field: string, rule: ErpInvoiceIssueRule, row?: number | null, value?: string): ValidationIssue => ({
  field,
  rule,
  ...(row !== null && row !== undefined && { row }),
  ...(value !== undefined && { value: value.slice(0, 200) }),
});

// --- Amounts ---

const cents = (value: Big) => value.round(2, Big.roundHalfUp);
const sum = (values: readonly Big[]) => values.reduce((total, value) => total.plus(value), new Big(0));

/** §7.4.3: the VAT of a line is its taxable amount times the rate of its code, rounded to the cent (half up). */
export const lineVat = (base: string, ratePct: string): string =>
  cents(new Big(base).times(ratePct).div(100)).toFixed(2);

/**
 * What a sales line is invoiced for (§7.4.2 #8): the units shipped at the net price of the order line, after the
 * discount of the line and of the header (`unitNet` of sales), rounded to the cent.
 */
export function salesLineBase(line: {
  quantity: number;
  unitPrice: string;
  discountPct: string | null;
  headerDiscountPct: string | null;
}): string {
  const net = new Big(line.unitPrice)
    .times(new Big(1).minus(new Big(line.discountPct ?? 0).div(100)))
    .times(new Big(1).minus(new Big(line.headerDiscountPct ?? 0).div(100)));
  return cents(net.times(line.quantity)).toFixed(2);
}

/** What a purchase line is invoiced for (§7.4.2 #8): the units invoiced at the cost of the PO line, to the cent. */
export const purchaseLineBase = (quantity: number, unitCost: string): string =>
  cents(new Big(unitCost).times(quantity)).toFixed(2);

/**
 * The deposit an invoice of an order deducts (C-50, V-18): the same share of each invoice as the deposit is of the
 * order (a 30% deposit covers 30% of each invoice), never more than what was received and not yet deducted by the
 * invoices before. TODO(V-18): the hypothesis of B-114, used only when Finance turns it on.
 */
export function proRataDeposit(p: {
  depositPct: string | null;
  depositReceived: string | null;
  deducted: string;
  invoiceNet: string;
}): string {
  if (p.depositPct === null || p.depositReceived === null) return "0.00";
  const share = cents(new Big(p.invoiceNet).times(p.depositPct).div(100));
  const left = new Big(p.depositReceived).minus(p.deducted);
  const deduction = share.lt(left) ? share : left;
  return (deduction.gt(0) ? deduction : new Big(0)).toFixed(2);
}

/**
 * Where a supplier is for its VAT (D-25): in the country of the company, elsewhere in the EU, or outside. The VAT code
 * of a purchase is the one Finance gives that place.
 */
export function vatScopeOf(p: {
  companyCountry: string;
  partyCountry: string | null;
  partyInEu: boolean;
}): Exclude<TaxDestination, "any"> | null {
  if (p.partyCountry === null) return null;
  if (p.partyCountry === p.companyCountry) return "domestic";
  return p.partyInEu ? "eu" : "extra_eu";
}

/** The place of the suppliers a VAT code of purchases is for (`reference.erp_vat_code.purchase_scope`): `any` is every place. */
export type PurchaseScope = TaxDestination;

/** VAT and total of each line, and of the document: what the file says in #10, #13 and #5. */
export interface ErpInvoiceTotals {
  lines: { vat: string; total: string }[];
  net: string;
  vat: string;
  total: string;
}

/** The totals of a document: a line whose code has no rate counts no VAT (`unknown_vat_code` stops its file). */
export function erpInvoiceTotals(doc: { lines: readonly ErpInvoiceLine[] }): ErpInvoiceTotals {
  const lines = doc.lines.map((line) => {
    // the file states the base with two decimals (#8): VAT and totals are of what it states
    const base = cents(new Big(line.base));
    const vat = line.vatRate === null ? new Big(0) : new Big(lineVat(base.toFixed(2), line.vatRate));
    return { base, vat, total: base.plus(vat) };
  });
  return {
    lines: lines.map((line) => ({ vat: line.vat.toFixed(2), total: line.total.toFixed(2) })),
    net: sum(lines.map((line) => line.base)).toFixed(2),
    vat: sum(lines.map((line) => line.vat)).toFixed(2),
    total: sum(lines.map((line) => line.total)).toFixed(2),
  };
}

/** The total the file states (#5): the supplier's, or the sum of the lines for a document Nexxt makes. */
export const erpInvoiceTotal = (doc: ErpInvoiceDocument): string =>
  doc.statedTotal === null ? erpInvoiceTotals(doc).total : new Big(doc.statedTotal).toFixed(2);

/**
 * What stops a document from its file (§7.4.1, §7.4.3), each issue on the line (`row`, its `lineNo`) or on the header:
 * the party and the number, a VAT code and its rate, the register, the account of a line that is not goods, the
 * warehouse, and the balance, the stated total equal to the sum of the line totals, to the cent.
 */
export function erpInvoiceIssues(doc: ErpInvoiceDocument): ValidationIssue[] {
  const header = [
    ...(doc.party === null ? [issue("party", "required")] : []),
    ...(doc.number === null ? [issue("number", "required")] : []),
    ...(/^[A-Z]{3}$/.test(doc.currency) ? [] : [issue("currency", "invalid_value", null, doc.currency)]),
    ...(doc.lines.length === 0 ? [issue("lines", "no_lines")] : []),
  ];
  const lines = doc.lines.flatMap((line) => [
    ...(line.vatCode === null ? [issue("vatCode", "missing_vat_code", line.lineNo)] : []),
    ...(line.vatCode !== null && line.vatRate === null
      ? [issue("vatCode", "unknown_vat_code", line.lineNo, line.vatCode)]
      : []),
    ...(line.protocol === null ? [issue("protocol", "missing_protocol", line.lineNo)] : []),
    ...(!line.goods && line.costAccount === null ? [issue("costAccount", "missing_cost_account", line.lineNo)] : []),
    ...(line.location !== null && line.location.length > ERP_LOCATION_MAX_LENGTH
      ? [issue("location", "location_too_long", line.lineNo, line.location)]
      : []),
    ...(line.quantity < 0 ? [issue("quantity", "invalid_value", line.lineNo, String(line.quantity))] : []),
  ]);
  const computed = erpInvoiceTotals(doc).total;
  const balanced = doc.statedTotal === null || new Big(doc.statedTotal).round(2, Big.roundHalfUp).eq(computed);
  const balance = balanced ? [] : [issue("total", "unbalanced", null, `${doc.statedTotal} ≠ ${computed}`)];
  return [...header, ...lines, ...balance];
}

// --- The text of the file (§7.4.1) ---

/** A value as the file takes it: no `;`, line break or tab inside, which the file has no quotes for. */
export const erpCell = (value: string | null): string =>
  value === null ? "" : value.replace(/\s*[;\r\n\t][;\s]*/g, " ").trim();

/** `gg/mm/aaaa`. */
const erpDate = (value: string | null): string => {
  if (value === null) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

/** Two decimals with the comma, no thousands separator (the `0,00` of the macro). */
export const erpAmount = (value: string): string => new Big(value).toFixed(2).replace(".", ",");

/** The rows of a document, each its 25 values in order. */
export function erpInvoiceRows(doc: ErpInvoiceDocument): string[][] {
  const totals = erpInvoiceTotals(doc);
  const total = erpAmount(erpInvoiceTotal(doc));
  return doc.lines.map((line, index) => {
    const computed = totals.lines[index] ?? { vat: "0", total: line.base };
    return [
      erpCell(doc.party),
      erpCell(doc.number),
      erpDate(doc.invoiceDate),
      erpDate(doc.postingDate),
      total,
      erpCell(doc.description),
      erpCell(line.description),
      erpAmount(line.base),
      erpCell(line.costAccount),
      erpAmount(computed.vat),
      erpCell(line.vatCode),
      erpCell(line.protocol),
      erpAmount(computed.total),
      erpCell(line.costCentre),
      doc.documentType,
      erpCell(doc.currency),
      erpCell(line.location),
      erpDate(line.accrualFrom),
      erpDate(line.accrualTo),
      erpCell(line.poNumber),
      String(line.quantity),
      erpCell(line.project),
      erpCell(line.season),
      erpCell(line.costCentreCode),
      erpCell(line.paymentMethod),
    ];
  });
}

/** `yyyyMMddHHmmss` in UTC. TODO(V-13): the hypothesis of T-78, UTC in the name of the file. */
const stamp = (at: Date) => at.toISOString().replace(/[-:T]/g, "").slice(0, 14);

/** `fatpass_…` for the purchase invoices, as the macro of today; `fatatt_…` for the sales (V-19, T-80). */
export const erpInvoiceFileName = (kind: ErpInvoiceKind, at: Date): string =>
  `${kind === "purchase" ? "fatpass" : "fatatt"}_${stamp(at)}.csv`;

/** The file, or why it cannot be written: the issues of each document, named by its number. */
export type ErpInvoiceFile =
  | { ok: true; fileName: string; content: string; rows: number; documents: number }
  | { ok: false; issues: { document: string | null; issues: ValidationIssue[] }[] };

/**
 * The file of documents of one kind and one company (§7.4.1): no header, `;` between the 25 values, CRLF after each
 * row. Written only if every document passes its check, and no two documents share the columns that make a document
 * (1-6, 15-16), which Business Central would merge into one. TODO(V-13): the text in UTF-8 without BOM (T-78); the
 * macro of today writes in the codepage of Windows.
 */
export function erpInvoiceFile(p: {
  kind: ErpInvoiceKind;
  at: Date;
  documents: readonly ErpInvoiceDocument[];
}): ErpInvoiceFile {
  const issues = p.documents
    .map((doc) => ({ document: doc.number, issues: erpInvoiceIssues(doc) }))
    .filter((d) => d.issues.length > 0);
  const keys = new Set<string>();
  for (const doc of p.documents) {
    const rows = erpInvoiceRows(doc);
    const first = rows[0];
    if (!first) continue;
    const key = [...first.slice(0, 6), ...first.slice(14, 16)].join(";");
    if (keys.has(key)) issues.push({ document: doc.number, issues: [issue("number", "duplicate_document")] });
    keys.add(key);
  }
  if (issues.length > 0 || p.documents.length === 0) {
    return { ok: false, issues };
  }
  const rows = p.documents.flatMap(erpInvoiceRows);
  return {
    ok: true,
    fileName: erpInvoiceFileName(p.kind, p.at),
    content: rows.map((row) => `${row.join(";")}\r\n`).join(""),
    rows: rows.length,
    documents: p.documents.length,
  };
}

/**
 * A document as its domain keeps it for the file (ADR-256): taken when the document leaves (the dispatch of the
 * inbound, the number of a sales invoice), with what stops it. A document taken complete does not change; one with
 * issues is taken again when asked, after Finance completes its tables.
 */
export const erpInvoiceSnapshot = z.object({
  document: erpInvoiceDocument,
  issues: z.array(validationIssue),
  takenAt: z.iso.datetime(),
  /**
   * The file the document went in once taken complete (spec v0.3 §7.5, ADR-258): handed to integration for the shared
   * folder and the queue of who imports it, then how the import went. Absent until it is handed over.
   */
  file: z
    .object({
      erpFileId: z.uuid(),
      name: z.string(),
      handedAt: z.iso.datetime(),
      status: z.enum(["to_import", "imported", "rejected"]),
      settledAt: z.iso.datetime().nullable(),
    })
    .optional(),
});
export type ErpInvoiceSnapshot = z.output<typeof erpInvoiceSnapshot>;

/**
 * How an invoice stands for the invoices file (ADR-256): ready, or what stops it; the totals the file says of it.
 * Backstage shows it next to the invoice, with the download when it is ready.
 */
export const erpInvoiceStateDto = z
  .object({
    ready: z.boolean(),
    issues: z.array(validationIssue),
    takenAt: z.iso.datetime(),
    number: z.string().nullable(),
    currency: z.string(),
    net: amount,
    vat: amount,
    total: amount,
    /** The file it went in, and how its import went (ADR-258); null until it is handed over. */
    file: erpInvoiceSnapshot.shape.file.unwrap().nullable(),
  })
  .meta({ id: "ErpInvoiceState" });
export type ErpInvoiceState = z.output<typeof erpInvoiceStateDto>;

/** The state of a document as its domain keeps it. */
export function erpInvoiceState(snapshot: ErpInvoiceSnapshot): ErpInvoiceState {
  const totals = erpInvoiceTotals(snapshot.document);
  return {
    ready: snapshot.issues.length === 0,
    issues: snapshot.issues,
    takenAt: snapshot.takenAt,
    number: snapshot.document.number,
    currency: snapshot.document.currency,
    net: totals.net,
    vat: totals.vat,
    total: erpInvoiceTotal(snapshot.document),
    file: snapshot.file ?? null,
  };
}

/**
 * Whether the domain takes a document for the file again (ADR-256): never once taken complete, so that it does not
 * change after Finance edits its tables; while it has issues, at a relaunch, a resend or when Finance asks for it. A
 * file Business Central refused is taken again once corrected, with the same number (spec v0.3 §7.4.3, ADR-258).
 */
export const erpInvoiceRetakes = (snapshot: ErpInvoiceSnapshot | null): boolean =>
  snapshot === null || snapshot.issues.length > 0 || snapshot.file?.status === "rejected";

/**
 * Whether a document taken complete has not named its file yet (ADR-258): its domain hands it over after the commit,
 * so an outcome of integration that names it comes before its file is recorded, and must come again. A take with
 * issues is never handed over, and a take that replaced another only follows one refused, whose outcome came.
 */
export const erpInvoiceAwaitsHandOver = (snapshot: ErpInvoiceSnapshot | null): boolean =>
  snapshot !== null && snapshot.issues.length === 0 && snapshot.file === undefined;

/** The key a document is handed over with, for its take: a take again after a refusal is a new file (ADR-258). */
export const erpInvoiceFileKey = (aggregate: string, id: string, takenAt: string): string =>
  `${aggregate}:${id}:${takenAt}`;

/**
 * A document of the file as its domain keeps it, taken now: with what stops it, the check of the file unless the
 * domain adds to it.
 */
export const takeErpInvoice = (
  document: ErpInvoiceDocument,
  at: Date,
  issues: ValidationIssue[] = erpInvoiceIssues(document),
): ErpInvoiceSnapshot => ({ document, issues, takenAt: at.toISOString() });
